<template>
  <div v-if="errorLoading" class="p-4">Unable to generate preview.</div>
  <div v-else>
    <div v-if="!allEditorsLoaded" class="p-4">
      <div class="d-flex items-center">
        <Spinner message="Initializing Editor..." />
      </div>
    </div>
    <iframe
      ref="previewEl"
      class="previewIFrame"
      :style="{ opacity: allEditorsLoaded ? 1 : 0 }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import Spinner from "@/components/Spinner.vue";
import { injectKey, injectUseEmailTemplates } from "./useEmailTemplates";

const emailTemplatesScope = injectUseEmailTemplates();
const {
  selectedTemplateKey,
  viewEmailTemplateNoCustomizations,
  templateCustomizations,
  resetCustomizationToDefault
} = emailTemplatesScope;
const loading = ref(false);
const errorLoading = ref(false);
const previewEl = ref<any>();
const editorIframeElements = ref<HTMLIFrameElement[]>([]);
const loadedEditorTotal = ref(0);
const editorMinHeight = 260;

const props = defineProps({
  hideGlobalMessage: {
    type: Boolean,
    default: false
  }
});

const templateKeys = computed(() => {
  if (!templateCustomizations.value) return [];
  return templateCustomizations.value.map((customization) => customization.key);
});

const allEditorsLoaded = computed(() => {
  if (templateKeys.value.length === 0) return true;
  return loadedEditorTotal.value >= editorIframeElements.value.length - 1;
});

function replaceIframeContent(content: string) {
  loadedEditorTotal.value = 0;
  const previewFrameDoc = previewEl.value!.contentWindow.document as Document;
  const extractedContent = {
    head: content.match(/<head[^>]*>[\s\S]*<\/head>/gi)?.toString() || "",
    body: content.match(/<body[^>]*>[\s\S]*<\/body>/gi)?.toString() || ""
  };

  // Step 1: Replace all {{ template_var }} with iframes. The url /email_content_editor
  // renders the content editor in its own page.
  templateKeys.value.forEach((templateKey) => {
    extractedContent.body = extractedContent.body.replaceAll(
      `{{${templateKey}}}`,
      `<iframe src="/email_content_editor" data-edit-content-key="${templateKey}" data-cy="${templateKey}" ></iframe>`
    );
  });

  // Step 2: Render the HTML. We also make sure to disable buttons and links
  // so that the preview doesn't load an unexpected page.
  previewFrameDoc.querySelector("body")!.innerHTML = extractedContent.body;
  previewFrameDoc.querySelector("head")!.innerHTML = `
    ${extractedContent.head}
    <style>
      a, button, input { pointer-events: none; }
      iframe { background: white !important; }
      footer iframe { background: transparent !important; }
      [data-edit-content-key] {
        display: block;
        min-height: ${editorMinHeight} !important;
        width: 100%;
        border: 0;
        resize: vertical;
        overflow: auto;
        margin-top: 20px;
        margin-bottom: 20px;
      }
    </style>
  `;
  editorIframeElements.value =
    previewEl.value!.contentWindow.document!.querySelectorAll("iframe");

  // Step 3: Initialize the content editors
  editorIframeElements.value.forEach((editorIframe) => {
    editorIframe.onload = () => handleEditorIframeReady(editorIframe);
  });
}

function handleEditorIframeReady(editorIframe: HTMLIFrameElement) {
  const editorKey = editorIframe.getAttribute("data-edit-content-key");
  const customization = templateCustomizations.value!.find(
    (customization) => customization.key === editorKey
  )!;
  const editorContentWindow = editorIframe.contentWindow as any;
  try {
    // The init function is defined in packs/vue/emailContentEditor.js
    editorContentWindow.init({
      props: {
        fullSize: true,
        hideGlobalMessage: props.hideGlobalMessage,
        modelValue: customization
      },
      provide: {
        [injectKey]: emailTemplatesScope
      },
      on: {
        "reset-to-default"(customization) {
          resetCustomizationToDefault(customization);
        }
      }
    });
    loadedEditorTotal.value = loadedEditorTotal.value + 1;
  } catch (e) {
    errorLoading.value = true;
  }
}

// Sometimes the description can span a few lines, so we have to recalculate
// the new min size of the iframe
function resizeIframes() {
  if (!previewEl.value) return;
  editorIframeElements.value.forEach((editorIframe) => {
    const height = Math.max(
      getElementHeight(editorIframe.contentWindow!.document.body),
      getElementHeight(
        editorIframe.contentWindow!.document.querySelector("html")!
      )
    );
    editorIframe.style.minHeight = `${
      height > editorMinHeight ? editorMinHeight : height
    }px`;
  });
}

async function handleUpdatePreviewEmailTemplate() {
  loading.value = true;
  errorLoading.value = false;
  try {
    const results = await viewEmailTemplateNoCustomizations();
    replaceIframeContent(results.content);
  } catch (e) {
    errorLoading.value = true;
  } finally {
    loading.value = false;
  }
}

function getElementHeight(el: HTMLElement) {
  if (!el) return 0;
  return Math.max(
    el.clientHeight || 0,
    el.scrollHeight || 0,
    el.offsetHeight || 0
  );
}

onMounted(() => {
  handleUpdatePreviewEmailTemplate();
});

watch(allEditorsLoaded, () => {
  resizeIframes();
});

// Update preview email template when the template key customizations change (used length for quick diff check)
watch(
  () => `${selectedTemplateKey.value}-${templateCustomizations.value.length}`,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      return handleUpdatePreviewEmailTemplate();
    }
  },
  { immediate: true }
);

defineExpose({
  resizeIframes
});
</script>

<style lang="scss" scoped>
iframe {
  border: none;
}
.previewIFrame {
  width: 100%;
  height: 100%;
}
.loading {
  text-align: center;
  padding: 30px;
}
</style>
